import React, { useState, useEffect } from "react";
import { FiPlus, FiCalendar, FiMapPin, FiBriefcase } from "react-icons/fi";
import { BiTrash, BiEdit } from "react-icons/bi";
import Modal from "@/components/modal";
import toast from "react-hot-toast";

function ExperienceModal({ isOpen, onClose, initialData, onSave }) {
  const [formData, setFormData] = useState({
    title: "",
    company: "",
    location: "",
    contract: "full-time",
    from: "",
    to: "",
    current: false,
    description: "",
  });

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setFormData({
          ...initialData,
          current: initialData.current || (!initialData.to && !!initialData.from),
        });
      } else {
        setFormData({
          title: "",
          company: "",
          location: "",
          contract: "full-time",
          from: "",
          to: "",
          current: false,
          description: "",
        });
      }
    }
  }, [isOpen, initialData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title || !formData.company || !formData.from) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (!formData.current && formData.to && new Date(formData.to) < new Date(formData.from)) {
      toast.error("End date cannot be before start date");
      return;
    }
    onSave({
      ...formData,
      to: formData.current ? "" : formData.to,
    });
    onClose();
  };

  const employmentTypes = [
    { value: "full-time", label: "Full-time" },
    { value: "part-time", label: "Part-time" },
    { value: "contract", label: "Contract" },
    { value: "internship", label: "Internship" },
    { value: "freelance", label: "Freelance" },
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="w-full max-w-xl">
      <div className="p-6">
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-900">
            {initialData ? "Edit Experience" : "Add Experience"}
          </h2>
          <p className="text-sm text-gray-500 mt-1">Add your work experience</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Job Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="input w-full"
              placeholder="e.g. Software Engineer"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Company <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.company}
              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              className="input w-full"
              placeholder="e.g. Acme Inc."
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="input w-full"
                placeholder="e.g. Barcelona, Spain"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Employment Type</label>
              <select
                value={formData.contract}
                onChange={(e) => setFormData({ ...formData, contract: e.target.value })}
                className="input w-full"
              >
                {employmentTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date <span className="text-red-500">*</span>
              </label>
              <input
                type="month"
                value={formData.from ? formData.from.slice(0, 7) : ""}
                onChange={(e) => setFormData({ ...formData, from: e.target.value + "-01" })}
                className="input w-full"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="month"
                value={formData.to ? formData.to.slice(0, 7) : ""}
                onChange={(e) => setFormData({ ...formData, to: e.target.value + "-01" })}
                className="input w-full disabled:bg-gray-100"
                disabled={formData.current}
              />
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.current}
              onChange={(e) => setFormData({ ...formData, current: e.target.checked, to: "" })}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
            />
            <span className="text-sm text-gray-700">I currently work here</span>
          </label>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="input w-full resize-y"
              rows={3}
              placeholder="Describe your responsibilities and achievements..."
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={onClose} className="btn btn-secondary cursor-pointer">
              Cancel
            </button>
            <button type="submit" className="btn btn-primary cursor-pointer">
              {initialData ? "Save Changes" : "Add Experience"}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}

export default function ExperienceSection({ values, setValues }) {
  const [showModal, setShowModal] = useState(false);
  const [editingExperience, setEditingExperience] = useState(null);
  const experiences = values.experiences || [];

  const handleSave = (exp) => {
    if (editingExperience) {
      const index = experiences.findIndex((e) => 
        e._id ? e._id === editingExperience._id : e === editingExperience
      );
      const updated = [...experiences];
      updated[index] = exp;
      setValues({ ...values, experiences: updated });
    } else {
      setValues({
        ...values,
        experiences: [...experiences, exp],
      });
    }
    setEditingExperience(null);
  };

  const handleEdit = (exp) => {
    setEditingExperience(exp);
    setShowModal(true);
  };

  const handleRemove = (exp) => {
    setValues({
      ...values,
      experiences: experiences.filter((e) => e !== exp),
    });
  };

  const handleAdd = () => {
    setEditingExperience(null);
    setShowModal(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString(undefined, { month: "short", year: "numeric" });
  };

  const getEmploymentTypeLabel = (contract) => {
    const types = {
      "full-time": "Full-time",
      "part-time": "Part-time",
      "contract": "Contract",
      "internship": "Internship",
      "freelance": "Freelance",
    };
    return types[contract] || contract;
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-6 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-50 text-purple-600 rounded-lg">
            <FiBriefcase className="w-5 h-5" />
          </div>
          <h2 className="text-lg font-bold text-gray-900">Experience</h2>
        </div>
        <button
          onClick={handleAdd}
          className="flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-3 py-1.5 rounded-lg transition-colors cursor-pointer"
        >
          <FiPlus className="w-4 h-4" />
          Add
        </button>
      </div>

      <div className="p-6">
        {experiences.length > 0 ? (
          <div className="space-y-4">
            {experiences.map((exp, index) => (
              <div
                key={exp._id || index}
                className="group relative p-4 rounded-xl border border-gray-100 bg-gray-50/50 hover:bg-white hover:border-gray-200 hover:shadow-sm transition-all"
              >
                <div className="flex gap-4">
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="text-base font-semibold text-gray-900">{exp.title}</h3>
                        <p className="text-sm text-gray-600 mt-0.5">{exp.company}</p>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                        <button
                          type="button"
                          onClick={() => handleEdit(exp)}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer"
                        >
                          <BiEdit className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemove(exp)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                        >
                          <BiTrash className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Meta info */}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-gray-500">
                      <div className="flex items-center gap-1.5">
                        <FiCalendar className="w-3.5 h-3.5" />
                        <span>
                          {formatDate(exp.from)} — {exp.current ? "Present" : formatDate(exp.to) || "Present"}
                        </span>
                      </div>
                      {exp.location && (
                        <div className="flex items-center gap-1.5">
                          <FiMapPin className="w-3.5 h-3.5" />
                          <span>{exp.location}</span>
                        </div>
                      )}
                      {exp.contract && (
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                          {getEmploymentTypeLabel(exp.contract)}
                        </span>
                      )}
                    </div>

                    {/* Description */}
                    {exp.description && (
                      <p className="text-sm text-gray-600 mt-3 leading-relaxed whitespace-pre-wrap">{exp.description}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
            <FiBriefcase className="w-8 h-8 mx-auto mb-3 text-gray-300" />
            <p className="font-medium text-gray-600">No experience added yet</p>
            <p className="text-sm mt-1">Add your work history to showcase your career</p>
          </div>
        )}
      </div>

      <ExperienceModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingExperience(null);
        }}
        initialData={editingExperience}
        onSave={handleSave}
      />
    </div>
  );
}
